import { useMemo } from 'react';
import { Roles } from '../../enums/Roles';
import type { Employee } from '../../models/Employee';
import type { Project } from '../../stores/ProjectsStore';

/**
 * Who may write on this board.
 *
 * Decision 4 of the spec: a shared-link visitor is *not* read-only — the link is the permission and
 * the server re-checks it on every write — so the public board behaves like the authenticated one.
 */
export function useReadOnly(
  project: Project | undefined,
  user: Employee | undefined,
  isAnonymous: boolean,
): boolean {
  return useMemo(() => {
    if (isAnonymous) return false;
    if (!user) return true;

    const isOwnerOrAdmin =
      user.roles?.some((role) => Number(role.id) === Roles.Owner || Number(role.id) === Roles.Admin) ?? false;
    if (isOwnerOrAdmin) return false;

    if (!project) return true;

    const members = Array.isArray(project.members) ? project.members : [];
    const membership = members.find((member) => String(member.id) === String(user.id));
    // A non-member may still write on a published project; that is what `isPublish` means here.
    if (!membership) return !(project.isPublish ?? false);

    const roleTitle = membership.role?.title ?? '';
    return roleTitle.toLowerCase() === 'viewer' || Number(membership.role?.id) === Roles.Viewer;
  }, [project, user, isAnonymous]);
}
